/**
 * Display formatting. Money is integer cents on the wire (08 §3); every instant is shown in the venue's
 * timezone, never the browser's. Event dates (`YYYY-MM-DD`) are already venue-local.
 */
import { TZDate } from '@date-fns/tz';
import { format, formatDistanceToNowStrict } from 'date-fns';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export function formatCents(cents: number | null | undefined, opts: { whole?: boolean } = {}): string {
  if (cents === null || cents === undefined) return '—';
  return (opts.whole && cents % 100 === 0 ? usdWhole : usd).format(cents / 100);
}

/** Dollars typed in a form → cents; empty → 0. */
export function dollarsToCents(value: string | number): number {
  const n = typeof value === 'number' ? value : Number.parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function formatPercent(pct: number): string {
  return `${pct}%`;
}

/** An instant in the venue zone, e.g. `Mon, Mar 3 · 6:00 PM`. */
export function formatInstant(iso: string | null | undefined, timezone: string, pattern = 'EEE, MMM d · h:mm a'): string {
  if (!iso) return '—';
  return format(new TZDate(iso, timezone), pattern);
}

export function formatTimeOnly(iso: string, timezone: string): string {
  return format(new TZDate(iso, timezone), 'h:mm a');
}

/** A `YYYY-MM-DD` event date, e.g. `Sun, Mar 15, 2026`. */
export function formatDate(date: string | null | undefined, pattern = 'EEE, MMM d, yyyy'): string {
  if (!date) return '—';
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return format(new Date(y, m - 1, d), pattern);
}

/** `HH:mm` → `6:00 PM`. */
export function formatTime(time: string | null | undefined): string {
  if (!time) return '—';
  const [h, m] = time.split(':').map(Number) as [number, number];
  return format(new Date(2000, 0, 1, h, m), 'h:mm a');
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const future = date.getTime() > Date.now();
  return `${future ? 'in ' : ''}${formatDistanceToNowStrict(date)}${future ? '' : ' ago'}`;
}

export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Today's venue-local date as `YYYY-MM-DD`. */
export function todayIn(timezone: string, now = new Date()): string {
  return toDateString(new TZDate(now, timezone));
}

export function toDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` + days, in the venue zone. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return toDateString(new Date(y, m - 1, d + days));
}

/** The venue-local day an instant falls on. */
export function instantToDate(iso: string, timezone: string): string {
  return toDateString(new TZDate(iso, timezone));
}

/** Midnight at the start of a venue-local date, as an ISO instant. */
export function dateToInstant(date: string, timezone: string, time = '00:00'): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  return new TZDate(y, m - 1, d, hh, mm, 0, timezone).toISOString();
}

export const STAGE_LABELS: Record<string, string> = {
  new: 'New',
  engaged: 'Engaged',
  qualified: 'Qualified',
  tentative: 'Tentative',
  proposed: 'Proposed',
  booked: 'Booked',
  balance_due: 'Balance due',
  completed: 'Completed',
  lost: 'Lost',
};

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const WEEKDAY_LABELS: Record<(typeof WEEKDAYS)[number], string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

/** Seed rows Daniel must confirm start with this (08 §8); the UI flags them. */
export const PLACEHOLDER_PREFIX = 'PLACEHOLDER —';
export const isPlaceholder = (text: string | null | undefined): boolean =>
  typeof text === 'string' && text.trimStart().startsWith('PLACEHOLDER');

/** Actor strings (`guest`, `concierge`, `system`, `staff:<id>`) → a display label. */
export function actorLabel(actor: string, staffNames: Record<string, string> = {}): string {
  if (actor.startsWith('staff:')) return staffNames[actor.slice(6)] ?? 'Staff';
  return { guest: 'Guest', concierge: 'Concierge', system: 'System' }[actor] ?? actor;
}

/**
 * AI spend in (fractional) cents: a local model is `$0`, a cheap step shows four decimals (`$0.0042`),
 * anything over a dollar shows cents.
 */
export function formatAiCost(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  if (cents === 0) return '$0';
  const dollars = cents / 100;
  if (dollars < 0.1) return `$${dollars.toFixed(4)}`;
  if (dollars < 1) return `$${dollars.toFixed(3)}`;
  return usd.format(dollars);
}

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

/** `5,840` → `5.8K`. */
export function formatCompact(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : compact.format(n);
}

/** A v4 uuid (client message ids); `crypto.randomUUID` where available. */
export function uuid(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
